import React from 'react';
import PropTypes from 'prop-types';
import Section from '../../../Section';
import Header from '../../../Header';
import BreadCrumb from '../../../BreadCrumb';
import DOMPurify from "dompurify/dist/purify.min";
import formatTime from '../../../../utils/formatTime';

declare const I18n;

const Event = ({ initialData }) => {
  const getCategoryColor = (categoryId: number): string => {
    const colorMap: { [key: number]: string } = {
      1: '#42C0C6',
      2: '#E70880',
      3: '#8DC862',
      4: '#FBAE17',
      5: '#97A165',
      6: '#42C0C6',
      7: '#FBAE17',
      8: '#8DC862',
      9: '#E70880',
    };

    return colorMap[categoryId] || '#97A165';
  };

  const { event, userType } = initialData;
  const isAdmin = !(userType && (userType === 'Learner' || userType === 'Facilitator'));

  const getTZAbbr = (dateStr?: string) => {
    const date = dateStr ? new Date(dateStr) : new Date();

    return new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short'
    })
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value || '';
  };

  const tzAbbr = getTZAbbr(event?.date);

  const DOMPurifyConfig = {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["target", "allow", "allowfullscreen", "frameborder", "scrolling", "src", "width", "height"],
  };

  return (
    <div className="event-details">
      <Header title={event.title} />
      <BreadCrumb
        locations={[
          { title: I18n.t('models.event.events'), url: `${isAdmin ? '/admin' : ''}/events` },
          { title: event.title, url: `${isAdmin ? '/admin' : ''}/events/${event.slug}` }
        ]}
      />
      {event.photo_url && (
        <div className="event-image">
          <img src={event.photo} alt={event.title} />
        </div>
      )}
      <div className="event_details__container">
        <div>
          <Section
            title={I18n.t('views.common.overview')}
          />
          <div
            className="section__body app-text"
            style={{ paddingLeft: "0px" }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(event.description, DOMPurifyConfig),
            }}
          />
        </div>
        {/* Side Card */}
        <aside className="event_details__side_container">
          <Section
            title={I18n.t('models.event.event_details')}
          />
          <div className="event_details__gap">
            {/* Time - Start and End time if applicable, otherwise have the date */}
            {(event.start_time || event.end_time) && (
              <div className="event_details__label_gap">
                <b>{I18n.t('models.event.attributes.time')}</b>
                <div className="event_details__labels">
                  <i className="fa-solid fa-clock" />
                  <span>
                    {event.start_time && formatTime(event.start_time)}
                    {event.start_time && event.end_time && ' - '}
                    {event.end_time && formatTime(event.end_time)}
                    {` (${tzAbbr})`}
                  </span>
                </div>
              </div>
            )}
            {/* Date */}
            {event.date &&
              <div className="event_details__label_gap">
                <b>{I18n.t('models.event.attributes.date')}</b>
                <div className="event_details__labels">
                  <i className="fa-solid fa-calendar" />
                  <span>{event.formatted_date}</span>
                </div>
              </div>
            }
            {/* Location */}
            {event.location &&
              <div className="event_details__label_gap">
                <b>{I18n.t('models.event.attributes.location')}</b>
                <div className="event_details__labels">
                  <i className="fa-solid fa-location-dot" />
                  <span>{event.location}</span>
                </div>
              </div>
            }

            {/* Categories */}
            {event.categories && event.categories.length > 0 &&
              <div className="event_details__label_gap">
                <b>{I18n.t('models.event.attributes.categories')}</b>
                <div className="event_details__labels">
                  {event.categories.map((category) => (
                    <span key={category.id} className="categories" style={{ backgroundColor: getCategoryColor(category.id) }}>{category.name}</span>
                  ))}
                </div>
              </div>
            }
            {/* Hosted By */}
            {event.hosted_by && (
              <div className="event_details__label_gap">
                <b>{I18n.t('models.event.attributes.hosted_by')}</b>
                <div className="event_details__labels">
                  <i className="fa-solid fa-people-group" />
                  <span>{event.hosted_by}</span>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}

Event.propTypes = {
  initialData: PropTypes.shape({
    event: PropTypes.object.isRequired,
    userType: PropTypes.string.isRequired
  }).isRequired
}

export default Event